import type {
  Device,
  DeviceStatus,
  LifecycleAction,
} from '@/features/device-management/types/deviceManagement';
import { ApiHttpError, ApiNetworkError, isCommitOutcomeUnknown } from '@/shared/api/client';
import { dayKeyInTimeZone } from '@/shared/lib/dateTime';

export type DeviceOperationalStatus = Extract<DeviceStatus, 'active' | 'inactive'>;

export function deviceEventDateFloor(
  device: Pick<Device, 'createdAt' | 'firstActivatedOn'>,
  timeZone: string,
): string {
  const createdOn = dayKeyInTimeZone(device.createdAt, timeZone);
  return device.firstActivatedOn && device.firstActivatedOn < createdOn
    ? device.firstActivatedOn
    : createdOn;
}

type DeviceLifecycleStatusKey = DeviceStatus | 'unknown';

const lifecycleActionsByStatus: Record<DeviceLifecycleStatusKey, readonly LifecycleAction[]> = {
  unknown: ['set-status', 'under-calibration', 'sent-for-repair', 'broken', 'moved', 'replaced'],
  onboarding: ['set-status', 'under-calibration', 'sent-for-repair', 'broken', 'moved', 'replaced'],
  active: ['set-status', 'under-calibration', 'sent-for-repair', 'broken', 'moved', 'replaced'],
  inactive: ['set-status', 'under-calibration', 'sent-for-repair', 'broken', 'moved', 'replaced'],
  'under-calibration': ['returned', 'sent-for-repair', 'broken', 'replaced'],
  'sent-for-repair': ['returned', 'broken', 'replaced'],
  broken: ['returned', 'sent-for-repair', 'replaced'],
  replaced: [],
};

const operationalStatuses: readonly DeviceOperationalStatus[] = ['active', 'inactive'];

/**
 * Resolves lifecycle actions from the backend-owned device state. A current shipment takes
 * precedence over the status transition table because it must be closed before other work.
 */
export function deviceLifecycleActions(device: Pick<Device, 'status' | 'currentShipment'>) {
  if (device.currentShipment) return ['received'] as const;
  return lifecycleActionsByStatus[device.status ?? 'unknown'];
}

export function deviceLifecycleStatusOptions(
  device: Pick<Device, 'status' | 'currentShipment'>,
): Array<{ value: DeviceOperationalStatus; disabled: boolean }> {
  if (!deviceLifecycleActions(device).includes('set-status')) return [];
  return operationalStatuses.map((status) => ({
    value: status,
    disabled: device.status === status,
  }));
}

export function lifecycleActionCreatesShipment(
  status: DeviceStatus | null,
  action: LifecycleAction,
) {
  if (action === 'under-calibration' || action === 'sent-for-repair' || action === 'moved') {
    return true;
  }
  return action === 'returned' && (status === 'under-calibration' || status === 'sent-for-repair');
}

export function deviceLifecycleErrorCode(error: unknown): string | undefined {
  if (error instanceof ApiNetworkError || isCommitOutcomeUnknown(error)) {
    return 'commit_outcome_unknown';
  }
  if (!(error instanceof ApiHttpError)) return undefined;
  if (error.payload === null || typeof error.payload !== 'object') return undefined;
  const code = (error.payload as Record<string, unknown>).error;
  return typeof code === 'string' ? code : undefined;
}

export function deviceLifecycleErrorField(error: unknown): string | undefined {
  if (!(error instanceof ApiHttpError)) return undefined;
  if (error.payload === null || typeof error.payload !== 'object') return undefined;
  const field = (error.payload as Record<string, unknown>).field;
  return typeof field === 'string' ? field : undefined;
}
